import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ChangeCircle, Save } from "@mui/icons-material";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

import Toast from "../../../components/Layout/Toast";
import { FormikTextField } from "../../../components/Layout/FormikTextField";
import { CompanyFilter } from "../../../components/filter/CompanyFilter";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectAuth } from "../../auth/authSlice";
import { ClientTypes } from "../../auth/types/authType";
import { Company } from "../../setups/types/setupTypes";
import {
  createEmployee,
  selectProcesses,
  setSelectedEmployee,
} from "../processSlices";
import { Employee, NotificationMessageTypes } from "../types/processTypes";

const limitedEditSchema = Yup.object({
  additionalData: Yup.object({
    labourId: Yup.string(),
  }),
});

interface Props {
  currentEmployee?: Employee;
  handleDialogClose: (saved?: boolean) => void;
}

export const EmployeeLimitedEditDialog = ({
  currentEmployee,
  handleDialogClose,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);
  const {
    selectedEmployee,
    error: saveFailed,
    success: saveSuccess,
  } = useAppSelector(selectProcesses);

  const [company, setCompany] = useState<Company>();
  const [showOfficeChange, setShowOfficeChange] = useState(false);

  useEffect(() => {
    if (currentEmployee?._id && currentEmployee._id.length > 10) {
      dispatch(setSelectedEmployee(currentEmployee));
      setShowOfficeChange(false);
      setCompany(undefined);
    }
  }, [currentEmployee, dispatch]);

  useEffect(() => {
    if (saveSuccess) {
      handleDialogClose(true);
    }
  }, [handleDialogClose, saveSuccess]);

  const employee = selectedEmployee ?? currentEmployee;

  return (
    <DialogContent sx={{ width: "100%", boxSizing: "border-box" }}>
      <Box sx={{ px: { xs: 0, md: 1 }, pt: 1 }}>
        <Formik
          enableReinitialize
          initialValues={employee as Employee}
          validationSchema={limitedEditSchema}
          onSubmit={(values, actions) => {
            actions.setSubmitting(false);

            let payload: Employee = {
              ...values,
              notificationMessage: NotificationMessageTypes.updated as string,
            };

            if (company) {
              payload = {
                ...payload,
                owner: company,
              };
              if (company.parentCompany) {
                payload = {
                  ...payload,
                  parentCompany: company.parentCompany,
                };
              }
            }

            dispatch(createEmployee(payload));
          }}
        >
          {(props: FormikProps<Employee>) => (
            <Form>
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormikTextField
                      formikKey="givenName"
                      label={t("candidate") + " " + t("fullName")}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormikTextField
                      formikKey="additionalData.labourId"
                      label="Labour ID"
                    />
                  </Grid>

                  {employee?.owner && !showOfficeChange && (
                    <Grid container item xs={12} sm={6} spacing={1} alignItems="center">
                      <Grid item xs={8}>
                        <FormikTextField
                          formikKey="owner.displayName"
                          label="Employee Office"
                          disabled
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Button
                          color="secondary"
                          variant="contained"
                          onClick={() => setShowOfficeChange(true)}
                          size="small"
                          fullWidth
                        >
                          <ChangeCircle sx={{ mr: 0.5 }} />
                          Change
                        </Button>
                      </Grid>
                    </Grid>
                  )}

                  {showOfficeChange &&
                    user?.company?.type === ClientTypes.Moderator && (
                      <Grid item xs={12} sm={6}>
                        <CompanyFilter
                          companyType={ClientTypes.LocalAgency}
                          setCompany={setCompany}
                          includeAll={false}
                        />
                      </Grid>
                    )}
                </Grid>

                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  fullWidth
                  disabled={!props.isValid}
                  startIcon={<Save />}
                >
                  {t("save")}
                </Button>

                {saveFailed && (
                  <Toast severity="error">{saveFailed.message}</Toast>
                )}
              </Stack>
            </Form>
          )}
        </Formik>
      </Box>
    </DialogContent>
  );
};
